import { useCallback, useMemo } from 'react';
import { ColorMap, PointCloudMaterialProps } from '@abyss/3d-viewer';

import { useColors } from './colors';
import { useNormalEffects, useImageViewpointEffects } from './ViewerModes';
import {
  selectedAssemblyId as selectedAssemblyIdAtom,
  hasRgb,
  selectedStructureDefectsLegends,
  nonSelectedDefectsIsEnabled as nonSelectedDefectsIsEnabledAtom,
} from '@/components/Analysis/state';
import { PICKABLE_COLOR, TRANSPARENT_COLOR } from '@/constants';
import { useDefectColorEffects } from './useDefectColorEffects';

import { usePointSize } from '@/hooks';
import { MaxIdByAttribuite } from '@/hooks/usePointCloudInfo';
import { useAtomValue } from 'jotai';

export function usePointCloudMaterials(maxIds: MaxIdByAttribuite | undefined) {
  const selectedAssemblyId = useAtomValue(selectedAssemblyIdAtom);
  const hasColor = useAtomValue(hasRgb);
  const defectLegends = useAtomValue(selectedStructureDefectsLegends);
  const nonSelectedDefectsIsEnabled = useAtomValue(nonSelectedDefectsIsEnabledAtom);

  const { pointSize, enableSizeAttenuation } = usePointSize();

  const defectLegendKeys = useMemo(() => {
    return defectLegends
      ? [...new Set(defectLegends.map((defectLegend) => defectLegend.octreeKey.toLowerCase()))]
      : [];
  }, [defectLegends]);

  const createColorMap = useCallback(
    (attributeName: string, defaultColor: number) => {
      if (!maxIds) return undefined;
      if (!(attributeName in maxIds)) {
        throw new Error(`Octree has no '${attributeName}' attribute`);
      }
      return new ColorMap(maxIds[attributeName], undefined, defaultColor);
    },
    [maxIds]
  );

  const annotationPickingMap = useMemo(() => {
    return createColorMap('annotation', PICKABLE_COLOR);
  }, [createColorMap]);

  // This sets the initial color map for points WITHOUT actually setting the colors themselves.
  // Color setting is handled by the various effects per viewer mode (see /ViewerModes/*/effects.ts)
  //
  // Doing it this way is a performance optimisation
  // - changing pointCloudMaterials is expensive,
  // - changing colors by using an existing pointCloudMaterial's colorMap.set is much faster

  const pointCloudMaterials = useMemo(() => {
    if (!maxIds) return undefined;
    const baseMaterial = {
      pointSize,
      enableSizeAttenuation,
      visibilityAttributeName: 'annotation',
    };
    const colorMap = createColorMap('annotation', TRANSPARENT_COLOR);
    const annotationMaterial = {
      ...baseMaterial,
      colorMap,
      attributeName: 'annotation',
      visibilityMapForPicking: annotationPickingMap,
      hasColor,
    };

    // NOTE: if no defect colours are defined and retrieved from the database an asset will not be visible
    //  when selected because of TRANSPARENT_COLOR

    const defectMaterials = new Map<string, PointCloudMaterialProps>([
      ['annotation', annotationMaterial],
    ]);
    if (defectLegends && defectLegends?.length > 0) {
      defectLegendKeys.forEach((octreeKey) => {
        const defectsColorMap = createColorMap(octreeKey, TRANSPARENT_COLOR);
        const material = {
          ...baseMaterial,
          colorMap: defectsColorMap,
          attributeName: octreeKey,
          visibilityMap: colorMap,
          visibilityMapForPicking: annotationPickingMap,
          hasColor: hasColor && nonSelectedDefectsIsEnabled,
        };
        defectMaterials.set(octreeKey, material);
      });
    }

    return defectMaterials;
  }, [
    maxIds,
    createColorMap,
    pointSize,
    enableSizeAttenuation,
    annotationPickingMap,
    hasColor,
    defectLegends,
    nonSelectedDefectsIsEnabled,
    defectLegendKeys,
  ]);

  // Define when to use which material
  const material = useMemo(() => {
    const selectedDefect = defectLegends?.find((defect) => defect.isEnabled);
    return selectedDefect?.octreeKey.toLowerCase() ?? 'annotation';
  }, [defectLegends]);

  const colorMap: ColorMap | undefined = useMemo(() => {
    const colorMapCheck = pointCloudMaterials?.get('annotation')?.colorMap;
    return colorMapCheck && 'setColor' in colorMapCheck ? colorMapCheck : undefined;
  }, [pointCloudMaterials]);

  const defectColorMaps: Record<string, ColorMap> = useMemo(() => {
    const maps: Record<string, ColorMap> = {};
    defectLegends?.forEach((defectLegend) => {
      const octreeKey = defectLegend.octreeKey.toLowerCase();
      const colorMapCheck = pointCloudMaterials?.get(octreeKey)?.colorMap;
      if (colorMapCheck && 'setColor' in colorMapCheck) {
        maps[octreeKey] = colorMapCheck;
      }
    });
    return maps;
  }, [pointCloudMaterials, defectLegends]);

  const { allColors } = useColors();

  useNormalEffects(colorMap, annotationPickingMap, allColors, selectedAssemblyId);
  useImageViewpointEffects(colorMap, annotationPickingMap, allColors, selectedAssemblyId);
  useDefectColorEffects(defectColorMaps);

  return { pointCloudMaterials, material };
}
